import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import * as headerConfigApi from '../api/headerConfigApi';
import { useSnackbar } from '../context/SnackbarContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageHeader from '../components/common/PageHeader';

export default function HeaderConfiguration() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const { showSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try { setConfigs(await headerConfigApi.getAll()); }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (config) => {
    try {
      await headerConfigApi.update(config.id, { isActive: !config.isActive });
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  const move = async (index, direction) => {
    const reordered = [...configs];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= reordered.length) return;
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const items = reordered.map((c, i) => ({ id: c.id, orderIndex: i + 1 }));
    try {
      await headerConfigApi.bulkReorder(items);
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  const openEdit = (config) => { setEditItem(config); setEditName(config.displayName); };

  const handleSave = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await headerConfigApi.update(editItem.id, { displayName: editName });
      showSnackbar('Header updated', 'success');
      setEditItem(null);
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader
        title="Header Configuration"
        subtitle="Customize column names, order, and visibility for Excel import/export and table views."
      />
      <Alert severity="info" sx={{ mb: 2 }}>
        Changes apply to Excel download, upload column mapping, and table headers. The <strong>Field Key</strong> is internal — only the <strong>Display Name</strong> is user-visible.
      </Alert>
      <Card>
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Field Key</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Reorder</TableCell>
              <TableCell align="right">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((c, i) => (
              <TableRow key={c.id} sx={{ opacity: c.isActive ? 1 : 0.5 }}>
                <TableCell>
                  <Chip label={c.orderIndex} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', color: '#6A1B9A', fontWeight: 600 }}>{c.fieldKey}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{c.displayName}</TableCell>
                <TableCell align="center">
                  <Switch checked={c.isActive} onChange={() => toggleActive(c)} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => move(i, 1)} disabled={i === configs.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Card>

      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Display Name</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Field Key: <strong>{editItem?.fieldKey}</strong>
          </Typography>
          <TextField
            autoFocus label="Display Name" fullWidth value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!editName.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
